// NOTE: ALL ROUTES in the SPA should have a corresponding route in config/routes.rb
// We do not route the catch-all route to the SPA anymore for performance reasons.
import { ENTERPRISE_GIFT_PARAM, GIFT_PARAM } from '@mc/constants'
import { isEnterpriseUser } from '@mc/user-info'

export const paths = {
  activate: '/activate',
  addOns: '/add-ons',
  acceleratorBundle: '/accelerator-bundle',
  appleTv: '/appletv',
  articles: '/articles',
  article: (articleSlug = ':articleSlug') => `/articles/${articleSlug}`,
  articlesBookmarks: '/articles-bookmarks',
  authConfirmEmail: '/auth/confirm-email',
  bogo: '/2-for-1-offer',
  bogoGift: (id = ':id') => `/bogo-gifts/${id}`,
  cancelSubscription: '/account/edit/cancel-subscription',
  cancelSubscriptionFeedback: '/account/edit/cancel-subscription/feedback',
  careers: 'https://careers.masterclass.com/',
  changePlanConfirmation: '/change-plan',
  chaptersWatch: (courseSlug = ':courseSlug', chapterSlug = ':chapterSlug') =>
    `/classes/${courseSlug}/chapters/${chapterSlug}`,
  chapterOrEpisodeWatch: (
    type: string,
    courseSlug = ':courseSlug',
    chapterSlug = ':chapterSlug',
  ) =>
    type === 'episode' || type === 'series'
      ? paths.episodesWatch(courseSlug, chapterSlug)
      : paths.chaptersWatch(courseSlug, chapterSlug),
  categories: '/categories',
  category: (categorySlug = ':categorySlug') => `/categories/${categorySlug}`,
  subcategory: (
    categorySlug: string = ':categorySlug',
    subcatSlug: string = ':subcatSlug',
  ) => `/categories/${categorySlug}#${subcatSlug}`,
  subcategoryPage: (subcategorySlug = ':subcategorySlug') =>
    `/subcategories/${subcategorySlug}`,
  checkout: '/checkout',
  cobrandLearnMore: '/cobrand-learn-more',
  checkoutPayment: (productId?: number | string, queryParams = '') => {
    if (!productId) return `/checkout?${queryParams ? `?${queryParams}` : ''}`

    return `/checkout/payment?product_id=${productId}${
      queryParams ? `&${queryParams}` : ''
    }`
  },
  checkoutGift: (query = {}) => {
    const queryParams = new URLSearchParams(query)
    queryParams.set(GIFT_PARAM, 'true')
    return `/checkout?${queryParams.toString()}`
  },
  checkoutEnterpriseGift: (query = {}) =>
    paths.checkoutGift({ ...query, [ENTERPRISE_GIFT_PARAM]: 'true' }),
  classPage: (courseSlug = ':courseSlug') => `/classes/${courseSlug}`,
  classOrSeriesPage: (type: string, courseSlug = ':courseSlug') =>
    type === 'series'
      ? paths.seriesPage(courseSlug)
      : paths.classPage(courseSlug),
  classRecommendations: '/find-my-classes',
  classRecommendationsResults: '/find-my-classes/results',
  classSelection: '/class-selection',
  content: (contentSlug = ':contentSlug') => `/content/${contentSlug}`,
  courseTypePage: (
    type: 'course' | 'series' | 'camp' | string,
    contentSlug: string,
  ) => {
    switch (type) {
      case 'camp':
        return paths.sessionsClassHome(contentSlug)
      case 'course':
        return paths.classPage(contentSlug)
      case 'series':
        return paths.seriesPage(contentSlug)
      default:
        return ''
    }
  },
  createProfile: '/profiles/create',
  discoverQuiz: '/discover-quiz',
  editProfile: (slug = ':slug') => `/profiles/${slug}/edit`,
  enterpriseHomepage: '/for-business',
  enterpriseAdmin: '/for-business/admin',
  enterpriseCart: '/for-business/cart',
  enterpriseProgressReporting: '/for-business/progress-reporting',
  enterprisePurchase: '/for-business/purchase',
  enterprisePurchaseLoading: '/for-business/purchase-loading',
  enterpriseReportsOverview: (orgId: string) =>
    `/for-business/admin/${orgId}/reports`,
  enterpriseReportsTopClasses: (orgId: string) =>
    `/for-business/admin/${orgId}/reports/top-classes`,
  enterpriseSharedContent: (orgId: string) =>
    `/for-business/admin/${orgId}/shared-content`,
  enterpriseTeams: (orgId: string, teamId: string) =>
    `/for-business/admin/${orgId}/teams/${teamId}`,
  enterpriseThankYou: '/for-business/thank-you',
  enterpriseCollective: '/for-business/collective',
  enterpriseHowItWorks: '/for-business/how-it-works',
  enterpriseAccelerator: '/for-business/accelerator',
  enterpriseSkillsLibrary: '/for-business/skills',
  enterpriseSkillCategory: (skillCategorySlug = ':skillCategorySlug') =>
    `/for-business/skills/${skillCategorySlug}`,
  enterpriseContactForm: '/for-business#contact-form',
  episodesWatch: (seriesSlug = ':courseSlug', episodeSlug = ':chapterSlug') =>
    `/series/${seriesSlug}/episodes/${episodeSlug}`,
  executive: '/executive',
  freeTrial: '/free-trial',
  gift: '/gift',
  giftsRedemption: (giftId = ':id', queryParams = '') =>
    `/gifts/${giftId}${queryParams ? `?${queryParams}` : ''}`,
  guestPass: '/guest-pass',
  guestPassRecipient: '/exclusive-guest-pass',
  guestPassTermsPath: 'https://learn.masterclass.com/guest-pass-offer-terms',
  home: '/homepage',
  tabbedHome: (tab = ':tab?') => `/homepage/${tab}`,
  inbox: '/inbox',
  inboxConversation: (conversationId = ':conversationId') =>
    `/inbox/${conversationId}`,
  instructorPortal: '/instructor-portal',
  krisJennerLive: `/kris-jenner-live`,
  library: '/library-search',
  logIn: (nextPage?: string) => {
    const encodedNextPage = nextPage ? encodeURIComponent(nextPage) : ''
    return `/auth/login${nextPage ? `?next_page=${encodedNextPage}` : ''}`
  },
  manageGifts: '/manage-gifts',
  memberWins: 'https://learn.masterclass.com/memberwins',
  mobilePush: '/mobile-push',
  mobileUniversalHome: '/home',
  myProgress: '/my-progress',
  onboarding: '/onboarding',
  onCall: 'https://oncall.masterclass.com',
  onCallLogin: 'https://oncall.masterclass.com/login',
  onCallCoach: 'https://www.masterclass.com/coach',
  onCallLoginCoach: 'https://www.masterclass.com/coach/login',
  outcomePage: (outcomeSlug = ':outcomeSlug') => `/outcomes/${outcomeSlug}`,
  outcomeOnSubcatPage: (
    subcatSlug = ':subcatSlug',
    outcomeSlug = ':outcomeSlug',
  ) => `/subcategories/${subcatSlug}#${outcomeSlug}`,
  package: (packageSlug = ':packageSlug') => `/packages/${packageSlug}`,
  pathTerms: '/terms/pathway-bundle-terms',
  changePlan: '/plans',
  playlists: '/playlists',
  playlistWatch: (
    playlistSlug = ':playlistSlug',
    trackSlug: string | null = null,
  ) => `/playlist/${playlistSlug}${trackSlug ? `?track=${trackSlug}` : ''}`,
  privacy: '//privacy.masterclass.com',
  privacyPolicies: '//privacy.masterclass.com/policies',
  privacySpeechPolicy:
    '//privacy.masterclass.com/policies?name=speech-to-text-notice ',
  profiles: '/profiles',
  programsPage: (programSlug = ':programSlug') => `/programs/${programSlug}`,
  projects: (slug: string) => `/projects/${slug}`,
  emailLandingPage: '/multi-product-promotion',
  promotion: '/promotion',
  promotionRedirect: (nextPath: string) =>
    `/promotion?next=${encodeURIComponent(nextPath)}`,
  redeem: '/redeem',
  redeemCheckout: '/redeem/checkout',
  refund: '/refund',
  root: '/',
  seriesPage: (seriesSlug = ':courseSlug') => `/series/${seriesSlug}`,
  sessions: () => '/sessions',
  sessionsClassHome: (campSlug = ':campSlug') =>
    `/sessions/classes/${campSlug}`,
  sessionsClassCommunity: (campSlug = ':campSlug') =>
    `/sessions/classes/${campSlug}/community`,
  sessionsClassCommunityWithModule: (
    campSlug = ':campSlug',
    moduleSlug = ':moduleSlug',
  ) => `/sessions/classes/${campSlug}/community/module/${moduleSlug}`,
  sessionsClassPost: (campSlug = ':campSlug', postId = ':postId') =>
    `/sessions/classes/${campSlug}/post/${postId}`,
  sessionsTaskPage: (campSlug = ':campSlug', taskSlug = ':taskSlug') =>
    `/sessions/classes/${campSlug}/tasks/${taskSlug}`,
  settings: '/account/edit',
  paymentHistory: '/account/payment-history',
  subscriptionPreferences: '/subscription-preferences',
  signUp: (nextPage?: string) =>
    `/auth/register${nextPage ? `?next_page=${nextPage}` : ''}`,
  sitemapCategories: (categorySlug: string) =>
    `/sitemap/categories/${categorySlug}`,
  sitemapCourses: (courseSlug: string) => `/sitemap/courses/${courseSlug}`,
  shortForm: (slug = ':slug') => `/ten-minutes/${slug}`,
  ssoLogIn: (nextPage?: string) =>
    `/auth/discovery${nextPage ? `?next_page=${nextPage}` : ''}`,
  verifyEmail: (token = ':token') => `/account/verify/${token}`,
  emailCorrectionResults: '/account/email-correction',
  terms: '/terms',
  termsForAI: '//learn.masterclass.com/ai-terms',
  giftTerms: 'https://learn.masterclass.com/gift-terms',
  giftAddOnTerms: 'https://learn.masterclass.com/addon-gift-promo-terms',
  certificates: '/certificates',
  certificatesHomepage: '/certificates/homepage',
  certificatesAnnualPass: '/certificates/annualpass',
  certificatesAnnualPassCheckout: '/certificates/annualpass/checkout',
  certificate: (certSlug: string = ':certSlug') => `/certificates/${certSlug}`,
  certificateLesson: (
    certSlug: string = ':certSlug',
    lessonSlug: string = ':lessonSlug',
  ) => `/certificates/${certSlug}/lesson/${lessonSlug}`,
  certificateOverview: (certSlug: string = ':certSlug') =>
    `/certificates/${certSlug}/overview`,
  certificateToolkit: (certSlug: string = ':certSlug') =>
    `/certificates/${certSlug}/toolkit`,
  certificateProjects: (certSlug: string = ':certSlug') =>
    `/certificates/${certSlug}/projects`,
  certificateEducatorOverview: (
    certSlug: string = ':certSlug',
    moduleSlug?: string,
  ) =>
    `/certificates/${certSlug}/educator/overview${
      moduleSlug ? `?module=${encodeURIComponent(moduleSlug)}` : ''
    }`,
  certificateEducatorTopic: (
    certSlug: string = ':certSlug',
    moduleSlug: string = ':moduleSlug',
    topicSlug: string = ':topicSlug',
  ) =>
    `/certificates/${certSlug}/educator/modules/${moduleSlug}/topics/${topicSlug}`,
  certificateQuiz: (certSlug: string = ':certSlug') =>
    `/certificates/${certSlug}/quiz`,
  certificatesCheckout: (certSlug: string = ':certSlug') =>
    `/certificates/${certSlug}/checkout`,
  certificatesAssetCheckout: (uuid: string, slug?: string) =>
    `/certificates/checkout?uuid=${uuid}${slug ? `&slug=${slug}` : ''}`,
  certificatesPayment: (productId: string | number) =>
    `/certificates/checkout/payment?product_id=${productId}`,
  certificatesLearnerGuidelines:
    'https://learn.masterclass.com/certificates-learner-guidelines',
  certificatesVanityTerms:
    'https://learn.masterclass.com/certificates-partner-discount-terms',
  certificatePathTerms:
    'https://www.masterclass.com/terms/Visionary-leadership-path-offer',
  // Help center links
  helpCenter: '/help-center',
  helpCenterMasterClass: '/help-center/masterclass',
  helpCenterMasterClassChat: '/help-center/masterclass/chat',
  helpCenterForProduct: (productSlug: string) => `/help-center/${productSlug}`,
  helpCenterChatForProduct: (productSlug: string) =>
    `/help-center/${productSlug}/chat`,
  helpCenterAnswer: (productSlug: string, answerSlug: string) =>
    `/help-center/${productSlug}/answers/${answerSlug}`,
  giftSupport:
    '/help-center/masterclass/answers/resending-editing-and-printing-your-gift--id--Gnx31xzCRe-Dp1X5VHTOQg',
  contact:
    '/help-center/masterclass/answers/Contacting-MasterClass-Support---id--6sH8j_CwT9iEZ_BJq_p6Sg',
  faq: '/help-center',
  gradingDisputeForm: '/help-center/certificates/grading-dispute-form',
  howCertificateGrading:
    '/help-center/certificates/answers/master-class-certificates-grading--id--ByFF6bjJRc6lFQPKvhxMmg',
  howToAppleTV:
    '/help-center/masterclass/answers/streaming-master-class-on-apple-tv--id--h6wtiv3BQv6dDftKRcKREg',
  howToAndroidTV:
    '/help-center/masterclass/answers/master-class-app-for-android-tv--id--10bGL6SJRfi9MOcRFNUlEA',
  howToAmazonTV:
    '/help-center/masterclass/answers/streaming-master-class-on-amazon-fire-tv--id--39PwWq5eSkuWXOlALumnlw',
  howToRokuTV:
    '/help-center/masterclass/answers/streaming-master-class-on-roku-t-vs--id--bZhx-UGUQxyBcXbQywPmpA',
  membershipFaq:
    '/help-center/masterclass/answers/about-master-class-membership-plans--id---KTOuuyVSJOaE7YwVZu2Og',
  howToRefund:
    '/help-center/masterclass/answers/requesting-a-refund--id--ffUda_F-TPqkca5o9hJc5g',
  howToGettingStartedWithRevolut:
    '/help-center/masterclass/answers/getting-started-with-revolut--id--ibWhhuMHTwSimH_2Qs58lQ',
  optusSubscriptionFAQ:
    '/help-center/masterclass-at-work/answers/master-class-at-work-faq--id--ThDJg40yTSKZCcK_9gsNFg',
  howCoursesMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  howUserMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  howAnalyticsOverviewMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  howTeamsMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  howEngagementMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  howUnlockWatchingDataInLeaderboardReport:
    '/help-center/masterclass-at-work/answers/admin-analytics-faq--id--omz_rz5-Sf6URASFnh4KuQ',
  howLeaderboardMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  masterClassAtWorkSharingContent:
    '/help-center/masterclass-at-work/answers/master-class-at-work-sharing-content--id--Tbu9P2d6Q8-BXRDEsv0t8Q',
  sharingExperience:
    '/help-center/masterclass-at-work/answers/master-class-at-work-sharing-content--id--Tbu9P2d6Q8-BXRDEsv0t8Q',
  howSkillsMetricsAreDefined:
    '/help-center/masterclass-at-work/answers/analytics-metrics-definitions--id--jpXF1pdTSESnHWo_breHCQ',
  masterClassAtWorkFaq:
    '/help-center/masterclass-at-work/answers/master-class-at-work-faq--id--ThDJg40yTSKZCcK_9gsNFg',
  supportBogo:
    '/help-center/masterclass/answers/sending-your-gift-for-buy-one-get-one-promotion--id--WVBxN-syT_GQ0HEn1ekzxA',
  supportGetRefund:
    '/help-center/masterclass/answers/requesting-a-refund--id--ffUda_F-TPqkca5o9hJc5g',
  giftNotReceivedBogo:
    '/help-center/masterclass/answers/troubleshooting-a-gift-that-was-not-received--id--Ada5iAO2Rf2JZbev3yI5RQ',
  cancelSubscriptionRenewal:
    '/help-center/masterclass/answers/cancelling-your-subscription-renewal--id--M2McWBvNTh-WNRR6llmkAw',
} as const

export const pathsWithQueryParams = {
  checkout: ({ isGift = false, query = {} }) => {
    const queryParams = new URLSearchParams(query)
    if (isGift) queryParams.set('gift', 'true')
    return `${paths.checkout}?${queryParams.toString()}`
  },
}

// Mobile Web Path Whitelist: Any path not on this list will instead render the
// Mobile-Push component for logged-in mobile web users.

// IMPORTANT: if you are adding a route that takes a param to this list (eg has an id in it),
// make sure it is formatted as a string in this array, not as a function.

// Example:
// export const mobileWebPathWhitelist = [
//  paths.articles,
//  paths.package(), <-- instead of paths.package, which is a function
//  ...
// ]

const baseAllowList = [
  paths.activate,
  paths.appleTv,
  paths.articles,
  paths.articlesBookmarks,
  paths.bogo,
  paths.bogoGift(),
  paths.cancelSubscription,
  paths.changePlan,
  paths.changePlanConfirmation,
  paths.gift,
  paths.guestPass,
  paths.guestPassRecipient,
  paths.krisJennerLive,
  paths.mobilePush,
  paths.refund,
  paths.settings,
  paths.paymentHistory,
  paths.profiles,
  paths.manageGifts,
  paths.certificateOverview(),
  paths.certificateLesson(),
  paths.addOns,
  paths.giftsRedemption(),
]

// TODO delete this conditional when consumer onboarding is available on mobile
if (isEnterpriseUser?.()) {
  baseAllowList.push(paths.onboarding)
}

export const mobileWebPathAllowList = baseAllowList
// Deprecated: use mobileWebPathAllowList instead. Will be removed after a deprecation period.
export const mobileWebPathWhitelist = baseAllowList
